import React, { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { debounce } from "@mui/material"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { useAppSelector } from "appRedux/hooks"
import { useLazySearchUserQuery } from "appRedux/slices/userApiSlice"
import { addViewedProfileToHistory, removeProfileFromHistory, updateSearchKeyword, updateUserSearchResult } from "appRedux/slices/exploreSlice"
import { FULFILLED, MEDIUM, PENDING, REQUEST_DEBOUNCE } from "constants/index"
import { Subtitle } from "components/atoms/Subtitle"
import UsersList from "components/organisms/UsersList"
import FadeInAnimation from "components/animations/FadeInAnimation"
import UserCard from "components/molecules/UserCard"
import CancelIcon from "components/atoms/_icons/CancelIcon"

export default function ExplorePage() {
  const [localLoading, setLocalLoading] = useState<boolean>(false)

  const searchKeyword = useAppSelector((state) => state.explore.searchKeyword)
  const userSearchResults = useAppSelector((state) => state.explore.userSearchResults)
  const recentVisitedProfiles = useAppSelector((state) => state.explore.recentVisitedProfiles)

  const [triggerSearchUser, { status }] = useLazySearchUserQuery()

  const dispatch = useDispatch()
  const { t } = useTranslation()

  useEffect(() => {
    if (status === FULFILLED) {
      if (localLoading) {
        setLocalLoading(false)
      }
    }
  }, [status])

  const renderRecentViewedProfilesList = useCallback(function () {
    return (
      (recentVisitedProfiles && recentVisitedProfiles?.length) ? <Stack overflow="scroll" gap="8px" padding="0 8px" flexGrow={1}>
        <Subtitle sx={{ margin: "8px 0" }}>{t("explore.recentSearches")}</Subtitle>
        {recentVisitedProfiles?.map((user, index) => (
          <FadeInAnimation key={user?.id}>
            <UserRow>
              <UserCard
                id={user?.id}
                handle={user?.handle}
                name={user?.name || ""}
                avatar={user?.avatar}
                avatarWidth={"46px"}
                onClick={() => handleOnUserCardClick(user)}
              />
              <Stack onClick={() => removeUserFromHistory(user)}>
                <CancelIcon/>
              </Stack>
            </UserRow>
          </FadeInAnimation>
        ))}</Stack> : null
    )
  }, [JSON.stringify(recentVisitedProfiles)])

  function removeUserFromHistory(user: UserDto) {
    dispatch(removeProfileFromHistory(user.handle))
  }

  const debouncedSearchUser = useCallback(debounce(function (value) {
    if (value.length < 1) return
    triggerSearchUser(value)
  }, REQUEST_DEBOUNCE), [])

  function handleSearchUser(searchedValue: string) {
    if (!localLoading && searchedValue) {
      setLocalLoading(true)
    }
    if (userSearchResults?.users?.length) {
      dispatch(updateUserSearchResult(null))
    }
    dispatch(updateSearchKeyword(searchedValue))
    debouncedSearchUser(searchedValue)
  }

  function handleOnUserCardClick(user: UserDto) {
    dispatch(addViewedProfileToHistory(user))
  }

  return (
    <UsersListWrapper id="users-list-wrapper">
      <UsersList
        defaultSearchValue={searchKeyword}
        users={userSearchResults?.users || null}
        loading={status === PENDING || localLoading}
        onSearch={handleSearchUser}
        clientSideSearch={false}
        searchInputSize={MEDIUM}
        emptySearchStatePlaceholder={renderRecentViewedProfilesList()}
        onClick={handleOnUserCardClick}
      />
    </UsersListWrapper>
  )
}

const UsersListWrapper = styled(Stack)(() => ({
  height: "calc(100vh - 98px)",
}))

const UserRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",

  "& svg": {
    width: "16px",
    height: "16px"
  },

  "& path": {
    fill: theme?.palette?.gray["14"]
  }
}))
